// src/components/subscriptions/PaymentMethodModal.js
import React, { useState } from 'react';
import './PaymentMethodModal.css';

const PaymentMethodModal = ({ gateways, amount, currency, breakdown, onSelect, onClose }) => {
    const [selectedGateway, setSelectedGateway] = useState(null);

    // Ensure gateways is always an array
    const gatewayArray = Array.isArray(gateways) ? gateways : [];

    const handleConfirm = () => {
        if (!selectedGateway) {
            alert('Please select a payment method');
            return;
        }
        onSelect(selectedGateway);
    };

    const getGatewayIcon = (type, code) => {
        const icons = {
            card: 'fas fa-credit-card',
            bank_transfer: 'fas fa-university',
            mobile_money: 'fas fa-mobile-alt',
            digital_wallet: 'fas fa-wallet',
            crypto: 'fab fa-bitcoin'
        };

        // Specific gateway icons
        const specificIcons = {
            paystack: 'fab fa-paypal',
            flutterwave: 'fas fa-money-bill-wave',
            stripe: 'fab fa-stripe',
            razorpay: 'fas fa-rupee-sign'
        };

        return specificIcons[code] || icons[type] || 'fas fa-credit-card';
    };

    const getGatewayDescription = (gateway) => {
        const descriptions = {
            paystack: 'Pay securely with your card via Paystack',
            flutterwave: 'Fast and secure card payments',
            stripe: 'International card payments powered by Stripe',
            razorpay: 'Indian payment gateway',
            jazzcash: 'Mobile wallet payment (Pakistan)',
            easypaisa: 'Mobile wallet payment (Pakistan)',
            bank_transfer: 'Direct bank transfer - Manual verification (1-3 business days)'
        };

        return descriptions[gateway.code] || `Pay using ${gateway.name}`;
    };

    const groupedGateways = gatewayArray.reduce((acc, gateway) => {
        if (!acc[gateway.type]) {
            acc[gateway.type] = [];
        }
        acc[gateway.type].push(gateway);
        return acc;
    }, {});

    const typeLabels = {
        card: 'Card Payment',
        bank_transfer: 'Bank Transfer',
        mobile_money: 'Mobile Money',
        digital_wallet: 'Digital Wallet',
        crypto: 'Cryptocurrency'
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="payment-method-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Select Payment Method</h3>
                    <button className="close-btn" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="payment-summary">
                    {breakdown ? (
                        <div className="payment-breakdown">
                            <div className="breakdown-header">Payment Summary</div>
                            <div className="breakdown-items">
                                {breakdown.subtotal > 0 && (
                                    <div className="breakdown-item">
                                        <span>Subtotal:</span>
                                        <span>{currency} {parseFloat(breakdown.subtotal).toFixed(2)}</span>
                                    </div>
                                )}
                                {breakdown.setup_fee > 0 && (
                                    <div className="breakdown-item">
                                        <span>Setup Fee:</span>
                                        <span>{currency} {parseFloat(breakdown.setup_fee).toFixed(2)}</span>
                                    </div>
                                )}
                                {breakdown.discount_amount > 0 && (
                                    <div className="breakdown-item discount">
                                        <span>Discount ({breakdown.discount_percentage}%):</span>
                                        <span>-{currency} {parseFloat(breakdown.discount_amount).toFixed(2)}</span>
                                    </div>
                                )}
                                {breakdown.tax_amount > 0 && (
                                    <div className="breakdown-item tax">
                                        <span>Tax ({breakdown.tax_percentage}%):</span>
                                        <span>{currency} {parseFloat(breakdown.tax_amount).toFixed(2)}</span>
                                    </div>
                                )}
                            </div>
                            <div className="breakdown-total">
                                <span>Total Amount:</span>
                                <span>{currency} {parseFloat(amount).toFixed(2)}</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="summary-label">Total Amount</div>
                            <div className="summary-amount">
                                {currency} {parseFloat(amount).toFixed(2)}
                            </div>
                        </>
                    )}
                </div>

                <div className="modal-body">
                    {gatewayArray.length === 0 ? (
                        <div className="empty-state">
                            <i className="fas fa-exclamation-circle"></i>
                            <h4>No Payment Methods Available</h4>
                            <p>Please contact support to set up payment gateways for your organization.</p>
                        </div>
                    ) : (
                        Object.entries(groupedGateways).map(([type, gatewaysList]) => (
                            <div key={type} className="gateway-group">
                                <h4 className="group-title">
                                    <i className={getGatewayIcon(type)}></i>
                                    {typeLabels[type] || type}
                                </h4>

                                <div className="gateway-list">
                                    {gatewaysList
                                        .filter(g => g.is_active)
                                        .map((gateway) => (
                                        <div
                                            key={gateway.id}
                                            className={`gateway-option ${selectedGateway === gateway.code ? 'selected' : ''}`}
                                            onClick={() => setSelectedGateway(gateway.code)}
                                        >
                                            <div className="gateway-radio">
                                                <input
                                                    type="radio"
                                                    name="gateway"
                                                    checked={selectedGateway === gateway.code}
                                                    onChange={() => { }}
                                                />
                                                <span className="radio-mark"></span>
                                            </div>

                                            <div className="gateway-icon">
                                                <i className={getGatewayIcon(gateway.type, gateway.code)}></i>
                                            </div>

                                            <div className="gateway-details">
                                                <div className="gateway-name">{gateway.name}</div>
                                                <div className="gateway-description">
                                                    {getGatewayDescription(gateway)}
                                                </div>
                                            </div>

                                            {gateway.code === 'bank_transfer' && (
                                                <div className="gateway-badge manual">Manual</div>
                                            )}

                                            {selectedGateway === gateway.code && (
                                                <div className="selected-icon">
                                                    <i className="fas fa-check-circle"></i>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    ))
                    )}

                    {gatewayArray.length > 0 && gatewayArray.filter(g => g.is_active).length === 0 && (
                        <div className="empty-state">
                            <i className="fas fa-exclamation-triangle"></i>
                            <p>No payment methods available for your region</p>
                            <small>Please contact support for assistance</small>
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="cancel-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="confirm-btn"
                        onClick={handleConfirm}
                        disabled={!selectedGateway || gatewayArray.length === 0}
                    >
                        <i className="fas fa-arrow-right"></i>
                        Continue to Payment
                    </button>
                </div>

                <div className="payment-security">
                    <i className="fas fa-lock"></i>
                    <span>Your payment is secured with SSL encryption</span>
                </div>
            </div>
        </div>
    );
};

export default PaymentMethodModal;